/**
 * Imperial unit helpers for the room visualizer.
 * Everything in the 3D scene is stored and measured in decimal feet;
 * these convert to/from the ft-in strings designers actually type.
 */

/** "5' 6"" | "5.5" | "66"" | "5 6" -> 5.5 (feet). Returns fallback when unparseable. */
export function parseFeet(raw, fallback = null) {
  if (raw === null || raw === undefined) return fallback;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;

  const s = String(raw).trim().replace(/[′’]/g, "'").replace(/[″”]/g, '"');
  if (!s) return fallback;

  // Inches only: 66", 66 in
  const inchesOnly = s.match(/^(-?[\d.]+)\s*(?:"|in\b|inch(?:es)?)$/i);
  if (inchesOnly) {
    const v = parseFloat(inchesOnly[1]);
    return Number.isFinite(v) ? v / 12 : fallback;
  }

  // Feet with optional inches: 5', 5' 6", 5ft 6in, 5 feet 6
  const feetInches = s.match(/^(-?[\d.]+)\s*(?:'|ft\b|feet|foot)\s*(?:([\d.]+)\s*(?:"|in\b|inch(?:es)?)?)?$/i);
  if (feetInches) {
    const ft = parseFloat(feetInches[1]);
    const inch = feetInches[2] ? parseFloat(feetInches[2]) : 0;
    if (!Number.isFinite(ft)) return fallback;
    return ft + (Number.isFinite(inch) ? inch / 12 : 0) * (ft < 0 ? -1 : 1);
  }

  // Bare "5 6" -> 5' 6"
  const bare = s.match(/^(-?[\d.]+)\s+([\d.]+)$/);
  if (bare) {
    const ft = parseFloat(bare[1]);
    const inch = parseFloat(bare[2]);
    if (Number.isFinite(ft) && Number.isFinite(inch)) return ft + (inch / 12) * (ft < 0 ? -1 : 1);
  }

  // Plain number -> feet
  const plain = parseFloat(s);
  return Number.isFinite(plain) ? plain : fallback;
}

/** 5.5 -> `5' 6"`. Rounds inches to the nearest 1/2" and carries 12" up to a foot. */
export function formatFeet(ft, { compact = false } = {}) {
  if (!Number.isFinite(ft)) return '';
  const sign = ft < 0 ? '-' : '';
  const abs = Math.abs(ft);
  let whole = Math.floor(abs);
  let inches = Math.round((abs - whole) * 12 * 2) / 2;
  if (inches >= 12) { whole += 1; inches -= 12; }
  const inchStr = Number.isInteger(inches) ? String(inches) : inches.toFixed(1);
  if (inches === 0) return `${sign}${whole}'`;
  return compact ? `${sign}${whole}'${inchStr}"` : `${sign}${whole}' ${inchStr}"`;
}

/** Short "4'0 x 8'0" label for a size readout. */
export function formatSize(wFt, hFt) {
  return `${formatFeet(wFt, { compact: true })} × ${formatFeet(hFt, { compact: true })}`;
}

/**
 * Pull a physical tile size out of a product's free-text `size` field so textures
 * can repeat at true scale. Handles: 24x48, 12" x 24", 2 x 8, 24 x 48 in, 3'x6'.
 * Returns { w, h } in INCHES, or null when nothing usable is present.
 */
export function parseTileSizeInches(size) {
  if (!size) return null;
  const s = String(size).trim().replace(/[′’]/g, "'").replace(/[″”]/g, '"');

  const m = s.match(/(-?[\d.]+)\s*("|'|in\b|inch(?:es)?|ft\b|feet)?\s*[x×]\s*(-?[\d.]+)\s*("|'|in\b|inch(?:es)?|ft\b|feet)?/i);
  if (!m) return null;

  const a = parseFloat(m[1]);
  const b = parseFloat(m[3]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;

  const unitOf = (u) => (u && /^('|ft|feet)$/i.test(u.trim()) ? 12 : 1);
  // A trailing unit applies to both numbers when only one is given.
  const unit = unitOf(m[4]) !== 1 ? unitOf(m[4]) : unitOf(m[2]);

  const w = a * unit;
  const h = b * unit;
  // Sanity: reject absurd values (a "size" field that was really a SKU).
  if (w > 240 || h > 240) return null;
  return { w, h };
}

/** Clamp helper used all over the drag math. */
export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
